<template>
  <div class="wrraper work_times">
    <Breadcrumb :items="breadcrumbItems" type="button" icon="fa-minus" />

    <transition name="fadeInUp" mode="out-in" v-if="loaderPage">
      <MainLoader />
    </transition>

    <template v-else>
      <!-- Toolbar -->
      <section class="work-toolbar">
        <div class="toolbar-card">
          <div class="toolbar-card__top">
            <div class="toolbar-card__modes">
              <div class="mode-switch">
                <button
                  type="button"
                  class="mode-btn"
                  :class="{ active: mode === 'global' }"
                  @click="mode = 'global'"
                >
                  <v-icon small>fal fa-globe</v-icon>
                  <span>{{ $t("workTime.modeGlobal") }}</span>
                </button>
                <button
                  type="button"
                  class="mode-btn"
                  :class="{ active: mode === 'branch' }"
                  @click="mode = 'branch'"
                >
                  <v-icon small>fal fa-store</v-icon>
                  <span>{{ $t("workTime.modeBranch") }}</span>
                </button>
              </div>
              <p class="hint-text">
                {{ mode === "global" ? $t("workTime.globalHint") : $t("workTime.branchHint") }}
              </p>
            </div>

            <div class="toolbar-card__branch" v-if="mode === 'branch'">
              <div class="input_wrapper top_label branch-select">
                <label class="form-label">{{ $t("workTime.selectBranch") }}</label>
                <multiselect
                  v-model="selectedBranch"
                  :options="branches"
                  label="name"
                  track-by="id"
                  :placeholder="$t('workTime.selectBranch')"
                  :searchable="true"
                  :allow-empty="false"
                  :show-labels="false"
                />
              </div>
            </div>

            <div class="toolbar-card__actions">
              <v-btn class="bulk-delete-btn" outlined depressed @click="openBulkDelete">
                <v-icon small left>fal fa-trash-alt</v-icon>
                {{ $t("workTime.bulkDelete") }}
              </v-btn>
            </div>
          </div>

          <div class="stats-row" v-if="canShowSchedule">
            <div class="stat-box">
              <div class="stat-box__icon stat-box__icon--days">
                <v-icon small color="white">fal fa-calendar-week</v-icon>
              </div>
              <div class="stat-box__content">
                <strong>{{ configuredDaysCount }}<small>/7</small></strong>
                <span>{{ $t("workTime.configuredDays") }}</span>
              </div>
            </div>
            <div class="stat-box">
              <div class="stat-box__icon stat-box__icon--slots">
                <v-icon small color="white">fal fa-clock</v-icon>
              </div>
              <div class="stat-box__content">
                <strong>{{ totalSlotsCount }}</strong>
                <span>{{ $t("workTime.totalSlots") }}</span>
              </div>
            </div>
            <div class="stat-box">
              <div class="stat-box__icon" :class="mode === 'global' ? 'stat-box__icon--global' : 'stat-box__icon--branch'">
                <v-icon small color="white">{{ mode === "global" ? "fal fa-globe" : "fal fa-store" }}</v-icon>
              </div>
              <div class="stat-box__content">
                <strong class="stat-box__mode">{{ mode === "global" ? $t("workTime.modeGlobal") : $t("workTime.modeBranch") }}</strong>
                <span v-if="mode === 'branch' && selectedBranch">{{ selectedBranch.name }}</span>
                <span v-else>{{ $t("workTime.applyToAllNote") }}</span>
              </div>
            </div>
          </div>

          <!-- Branches with custom schedules (global mode) -->
          <div
            v-if="mode === 'global' && customBranches.length"
            class="custom-branches-panel"
          >
            <div class="custom-branches-panel__head">
              <v-icon x-small class="mr-1">fal fa-code-branch</v-icon>
              <span>{{ $t("workTime.customBranchesTitle") }}</span>
              <span class="custom-branches-panel__count">{{ customBranches.length }}</span>
            </div>
            <p class="custom-branches-panel__hint">{{ $t("workTime.customBranchesHint") }}</p>
            <div class="custom-branches-panel__list">
              <button
                v-for="branch in customBranches"
                :key="'custom-' + branch.id"
                type="button"
                class="custom-branch-chip"
                @click="openCustomBranch(branch)"
              >
                <v-icon x-small>fal fa-store</v-icon>
                <span class="custom-branch-chip__name">{{ branch.name }}</span>
                <span class="custom-branch-chip__meta">
                  {{ $t("workTime.customDaysCount", { count: branch.different_days_count }) }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Empty: pick branch -->
      <section v-if="mode === 'branch' && !selectedBranch" class="empty-panel">
        <div class="empty-panel__card">
          <div class="empty-panel__icon">
            <v-icon size="40" color="primary">fal fa-store</v-icon>
          </div>
          <h5>{{ $t("workTime.selectBranch") }}</h5>
          <p>{{ $t("workTime.branchHint") }}</p>
        </div>
      </section>

      <!-- Schedule -->
      <section class="schedule-section" v-else-if="canShowSchedule">
        <!-- Quick week nav -->
        <div class="week-strip">
          <button
            v-for="(row, idx) in work_days"
            :key="'strip-' + row.day"
            type="button"
            class="week-strip__item"
            :class="{
              active: activeDayTab === idx,
              configured: row.times.length > 0,
            }"
            @click="activeDayTab = idx"
          >
            <span class="week-strip__name">{{ row.name }}</span>
            <span class="week-strip__dot" v-if="row.times.length">{{ row.times.length }}</span>
          </button>
        </div>

        <div class="tabs-wrapper">
          <v-tabs
            v-model="activeDayTab"
            background-color="transparent"
            color="primary"
            class="weekday-tabs"
            show-arrows
            height="48"
          >
            <v-tab v-for="row in work_days" :key="row.day">
              <span class="tab-day-name">{{ row.name }}</span>
              <span class="tab-badge" :class="{ empty: !row.times.length }">
                {{ row.times.length }}
              </span>
            </v-tab>
          </v-tabs>
        </div>

        <v-card class="day-panel" elevation="0" v-if="activeDay">
          <div class="day-panel__header">
            <div class="day-panel__info">
              <div class="day-panel__icon">
                <v-icon color="white">fal fa-calendar-day</v-icon>
              </div>
              <div>
                <h4 class="day-panel__title">{{ activeDay.name }}</h4>
                <span class="day-panel__meta">
                  {{
                    activeDay.times.length
                      ? $t("workTime.slotsCount", { count: activeDay.times.length })
                      : $t("titles.noWorkTimesForThisDay")
                  }}
                </span>
              </div>
            </div>
            <div class="day-panel__actions">
              <v-btn class="action-btn action-btn--primary" depressed @click="editItem(activeDay)">
                <v-icon small left>fal fa-edit</v-icon>
                {{ activeDay.times.length ? $t("titles.add/editWorkTimes") : $t("workTime.addSlot") }}
              </v-btn>
              <v-btn
                class="action-btn action-btn--danger"
                outlined
                :disabled="!activeDay.times.length"
                @click="deleteDay(activeDay)"
              >
                <v-icon small>fal fa-trash</v-icon>
              </v-btn>
            </div>
          </div>

          <div
            v-if="mode === 'global' && activeDay.custom_branches && activeDay.custom_branches.length"
            class="day-custom-branches"
          >
            <span class="day-custom-branches__label">{{ $t("workTime.customOnThisDay") }}:</span>
            <button
              v-for="branch in activeDay.custom_branches"
              :key="'day-custom-' + branch.id"
              type="button"
              class="day-custom-branch-tag"
              @click="openCustomBranch(branch)"
            >
              {{ branch.name }}
            </button>
          </div>

          <div class="day-panel__body" v-if="activeDay.times.length">
            <v-data-table
              class="elevation-0 diff_table day-table"
              :headers="headers"
              :items="activeDay.times"
              :loading="loading"
              item-key="id"
              hide-default-footer
              dense
            >
              <template v-slot:[`item.index`]="{ index }">
                <span class="row-index">{{ index + 1 }}</span>
              </template>
              <template v-slot:[`item.time_range`]="{ item }">
                <span class="time-range">
                  <span class="time-range__from">{{ item.from }}</span>
                  <v-icon x-small class="time-range__arrow">fal fa-long-arrow-right</v-icon>
                  <span class="time-range__to">{{ item.to }}</span>
                </span>
              </template>
              <template v-slot:[`item.price`]="{ item }">
                <span class="price-cell">{{ item.price }}</span>
              </template>
              <template v-slot:[`item.is_active`]="{ item }">
                <span class="status" :class="item.is_active ? 'success' : 'canceled'">
                  {{ item.is_active ? $t("active") : $t("inactive") }}
                </span>
              </template>
              <template v-slot:[`item.is_free`]="{ item }">
                <span class="status" :class="item.is_free ? 'paid' : 'unpaid'">
                  {{ item.is_free ? $t("titles.yes") : $t("titles.no") }}
                </span>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn icon x-small class="table-delete-btn" @click="deleteSlot(item)">
                  <v-icon x-small>fal fa-trash</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </div>

          <div v-else class="day-empty">
            <div class="day-empty__inner">
              <v-icon size="48" class="day-empty__icon">fal fa-calendar-plus</v-icon>
              <p>{{ $t("titles.noWorkTimesForThisDay") }}</p>
              <v-btn class="action-btn action-btn--primary" depressed @click="editItem(activeDay)">
                <v-icon small left>fal fa-plus</v-icon>
                {{ $t("workTime.addSlot") }}
              </v-btn>
            </div>
          </div>
        </v-card>
      </section>

      <!-- Add / Edit modal -->
      <v-dialog
        v-model="dialogEdit"
        max-width="720px"
        scrollable
        persistent
        content-class="work-time-dialog"
      >
        <v-card v-if="itemtoEdit" class="edit-modal">
          <div class="edit-modal__header">
            <div class="edit-modal__header-content">
              <div class="edit-modal__day-icon">
                <v-icon color="white" small>fal fa-calendar-day</v-icon>
              </div>
              <div>
                <span class="edit-modal__label">{{ $t("workTime.editModalTitle") }}</span>
                <h3 class="edit-modal__title">{{ itemtoEdit.name }}</h3>
              </div>
            </div>
            <v-btn icon dark class="edit-modal__close" @click="closeEditModal">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>

          <v-card-text class="edit-modal__body">
            <div class="scope-banner" :class="mode">
              <v-icon x-small>{{ mode === "global" ? "fal fa-globe" : "fal fa-store" }}</v-icon>
              <span>{{ mode === "global" ? $t("workTime.applyToAllNote") : $t("workTime.branchOnlyNote") }}</span>
              <span v-if="mode === 'branch' && selectedBranch" class="branch-tag">{{ selectedBranch.name }}</span>
            </div>

            <form @submit.prevent="saveForm" id="work-time-form">
              <div
                class="slot-card"
                v-for="(row, index) in itemtoEdit.times"
                :key="row.uid || row.id"
              >
                <div class="slot-card__accent"></div>
                <div class="slot-card__inner">
                  <div class="slot-card__head">
                    <div class="slot-card__num-wrap">
                      <span class="slot-card__num">{{ index + 1 }}</span>
                      <span class="slot-card__label">{{ $t("workTime.slotNumber", { n: index + 1 }) }}</span>
                    </div>
                    <v-btn
                      icon
                      x-small
                      class="slot-card__remove"
                      :disabled="itemtoEdit.times.length === 1"
                      @click="removeSlot(row, index)"
                    >
                      <v-icon x-small>fal fa-trash</v-icon>
                    </v-btn>
                  </div>

                  <div class="slot-card__time-row">
                    <div class="slot-field">
                      <label class="field-label">{{ $t("workTime.table.from") }}</label>
                      <v-menu
                        :ref="`time_from_${index}`"
                        v-model="row.time_from_modal"
                        :close-on-content-click="false"
                        :return-value.sync="row.from"
                        offset-y
                        max-width="290px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <div class="time-field" :class="{ filled: row.from }" v-bind="attrs" v-on="on">
                            <v-icon x-small>fal fa-clock</v-icon>
                            <span>{{ row.from || "--:--" }}</span>
                          </div>
                        </template>
                        <v-time-picker v-if="row.time_from_modal" v-model="row.from" format="24hr" full-width>
                          <v-btn text @click="row.time_from_modal = false">{{ $t("cancel") }}</v-btn>
                          <v-btn text color="primary" @click="saveTimeFrom(row.from, index)">{{ $t("save") }}</v-btn>
                        </v-time-picker>
                      </v-menu>
                    </div>
                    <div class="slot-field">
                      <label class="field-label">{{ $t("workTime.table.to") }}</label>
                      <v-menu
                        :ref="`time_to_${index}`"
                        v-model="row.time_to_modal"
                        :close-on-content-click="false"
                        :return-value.sync="row.to"
                        offset-y
                        max-width="290px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <div class="time-field" :class="{ filled: row.to }" v-bind="attrs" v-on="on">
                            <v-icon x-small>fal fa-clock</v-icon>
                            <span>{{ row.to || "--:--" }}</span>
                          </div>
                        </template>
                        <v-time-picker v-if="row.time_to_modal" v-model="row.to" format="24hr" full-width>
                          <v-btn text @click="row.time_to_modal = false">{{ $t("cancel") }}</v-btn>
                          <v-btn text color="primary" @click="saveTimeTo(row.to, index)">{{ $t("save") }}</v-btn>
                        </v-time-picker>
                      </v-menu>
                    </div>
                  </div>

                  <div v-if="isOvernightSlot(row)" class="overnight-hint">
                    <v-icon x-small>fal fa-moon</v-icon>
                    <span>
                      {{ $t("workTime.overnightSplitHint", {
                        day: itemtoEdit.name,
                        nextDay: $t(getNextWeekday(itemtoEdit.day)),
                        to: row.to,
                      }) }}
                    </span>
                  </div>

                  <div class="row g-2 mt-1">
                    <div class="col-sm-6">
                      <div class="input_wrapper top_label slot-input">
                        <input type="number" class="form-control" v-model.number="row.num_of_orders" min="1" />
                        <label class="form-label">{{ $t("titles.ordersNum") }}</label>
                      </div>
                    </div>
                    <div class="col-sm-6">
                      <div class="input_wrapper top_label slot-input">
                        <input type="number" class="form-control" v-model.number="row.price" min="0" step="0.01" />
                        <label class="form-label">{{ $t("titles.price") }}</label>
                      </div>
                    </div>
                    <div class="col-12">
                      <div class="slot-toggles">
                        <label class="toggle-pill" :class="{ on: row.is_active }">
                          <input type="checkbox" v-model="row.is_active" hidden />
                          <v-icon x-small>{{ row.is_active ? "fal fa-check" : "fal fa-times" }}</v-icon>
                          {{ $t("forms.labels.is_active") }}
                        </label>
                        <label class="toggle-pill" :class="{ on: row.is_free }">
                          <input type="checkbox" v-model="row.is_free" hidden />
                          <v-icon x-small>{{ row.is_free ? "fal fa-gift" : "fal fa-tag" }}</v-icon>
                          {{ $t("forms.labels.is_free") }}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <button type="button" class="add-slot-btn" @click="appendSlot">
                <v-icon small>fal fa-plus</v-icon>
                <span>{{ $t("workTime.addAnotherSlot") }}</span>
              </button>
            </form>
          </v-card-text>

          <div class="edit-modal__footer">
            <button
              type="button"
              class="edit-modal__btn edit-modal__btn--cancel"
              @click="closeEditModal"
              :disabled="btnIsLoading"
            >
              <v-icon x-small>fal fa-times</v-icon>
              {{ $t("cancel") }}
            </button>
            <button
              type="submit"
              form="work-time-form"
              class="edit-modal__btn edit-modal__btn--save"
              :disabled="btnIsLoading"
            >
              <v-icon x-small v-if="!btnIsLoading">fal fa-check</v-icon>
              <span class="edit-modal__btn-spinner" v-if="btnIsLoading"></span>
              {{ $t("forms.submit") }}
            </button>
          </div>
        </v-card>
      </v-dialog>

      <!-- Delete confirm -->
      <v-dialog v-model="dialogDelete" max-width="420" content-class="work-time-dialog">
        <v-card class="confirm-dialog">
          <v-card-title class="justify-center">
            <v-icon color="warning" class="mr-2">fal fa-exclamation-triangle</v-icon>
            {{ $t("table.deletedialog.areYouSure") }}
          </v-card-title>
          <v-card-actions class="justify-center pb-4">
            <v-btn outlined @click="dialogDelete = false">{{ $t("table.deletedialog.cancel") }}</v-btn>
            <v-btn color="error" depressed :loading="btnIsLoading" @click="confirmDelete">
              {{ $t("table.deletedialog.ok") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Bulk delete -->
      <v-dialog v-model="dialogBulkDelete" max-width="480" content-class="work-time-dialog">
        <v-card class="confirm-dialog">
          <v-card-title>{{ $t("workTime.bulkDeleteTitle") }}</v-card-title>
          <v-card-text>
            <v-radio-group v-model="bulkDeleteType" hide-details class="mt-0">
              <v-radio :label="$t('workTime.deleteAllBranches')" value="all" color="primary" />
              <v-radio :label="$t('workTime.deleteSelectedBranches')" value="branches" color="primary" />
            </v-radio-group>
            <multiselect
              v-if="bulkDeleteType === 'branches'"
              v-model="bulkDeleteBranches"
              :options="branches"
              label="name"
              track-by="id"
              :multiple="true"
              :searchable="true"
              :show-labels="false"
              class="mt-3"
            />
          </v-card-text>
          <v-card-actions class="px-4 pb-4">
            <v-spacer />
            <v-btn text @click="dialogBulkDelete = false">{{ $t("cancel") }}</v-btn>
            <v-btn color="error" depressed :loading="btnIsLoading" @click="confirmBulkDelete">
              {{ $t("table.deletedialog.ok") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

const WEEKDAYS = ["saturday", "sunday", "monday", "tuesday", "wednesday", "thursday", "friday"];

const SLOT_DEFAULTS = {
  num_of_orders: 1000,
  price: 0,
  is_free: false,
  is_active: true,
};

export default {
  data() {
    return {
      mode: "global",
      selectedBranch: null,
      branches: [],
      work_days: [],
      activeDayTab: 0,
      loading: false,
      loaderPage: false,
      btnIsLoading: false,
      dialogEdit: false,
      dialogDelete: false,
      dialogBulkDelete: false,
      itemtoEdit: null,
      deleteTarget: null,
      deleteType: null,
      bulkDeleteType: "all",
      bulkDeleteBranches: [],
      customBranches: [],
      isSyncingBranch: false,
    };
  },

  computed: {
    ...mapGetters({ lang: "lang_module/lang" }),

    canShowSchedule() {
      return this.mode === "global" || (this.mode === "branch" && this.selectedBranch);
    },

    activeDay() {
      return this.work_days[this.activeDayTab] || null;
    },

    configuredDaysCount() {
      return this.work_days.filter((d) => d.times.length > 0).length;
    },

    totalSlotsCount() {
      return this.work_days.reduce((sum, d) => sum + d.times.length, 0);
    },

    breadcrumbItems() {
      const items = [
        { text: this.$t("breadcrumb.mainPage"), disabled: false, href: "/" },
        { text: this.$t("breadcrumb.work-time.title"), disabled: false, href: "/work-time/show-all" },
      ];
      if (this.mode === "branch" && this.selectedBranch) {
        items.push({ text: this.selectedBranch.name, disabled: true, href: "" });
      } else {
        items.push({ text: this.$t("workTime.modeGlobal"), disabled: true, href: "" });
      }
      return items;
    },

    headers() {
      const t = (k) => this.$t(`workTime.table.${k}`);
      return [
        { text: "#", value: "index", align: "center", width: "48" },
        { text: t("from") + " – " + t("to"), value: "time_range", align: "center" },
        { text: t("price"), value: "price", align: "center", width: "80" },
        { text: t("numOfOrders"), value: "num_of_orders", align: "center", width: "100" },
        { text: t("isFree"), value: "is_free", align: "center", width: "80" },
        { text: t("isActive"), value: "is_active", align: "center", width: "80" },
        { text: t("actions"), value: "actions", align: "center", width: "56" },
      ];
    },
  },

  watch: {
    mode() {
      this.activeDayTab = 0;
      this.setRows();
    },
    selectedBranch(branch) {
      if (this.isSyncingBranch || this.mode !== "branch") return;
      const query = { ...this.$route.query };
      if (branch?.id) {
        query.branch_id = String(branch.id);
      } else {
        delete query.branch_id;
      }
      this.$router.replace({ path: this.$route.path, query }).catch(() => {});
    },
    "$route.query.branch_id"(id) {
      if (id) {
        this.mode = "branch";
        this.isSyncingBranch = true;
        this.syncBranchFromRoute();
        this.$nextTick(() => { this.isSyncingBranch = false; });
      }
      if (this.canShowSchedule) this.setRows();
    },
    lang() {
      this.work_days = this.buildEmptyWorkDays();
      if (this.canShowSchedule) this.setRows();
    },
  },

  async created() {
    this.work_days = this.buildEmptyWorkDays();
    await this.loadBranches();
    if (this.$route.query.branch_id) {
      this.mode = "branch";
      this.syncBranchFromRoute();
    }
    if (this.canShowSchedule) await this.setRows();
  },

  methods: {
    buildEmptyWorkDays() {
      return WEEKDAYS.map((day) => ({
        name: this.$t(day),
        day,
        id: null,
        times: [],
        branches: [],
        custom_branches: [],
      }));
    },

    resetWorkDays() {
      this.work_days = this.buildEmptyWorkDays();
    },

    async loadBranches() {
      const res = await this.$axios.get("get_all_branch");
      this.branches = (res.data.data || []).map((b) => ({
        id: b.id,
        name: b.full_name,
      }));
    },

    syncBranchFromRoute() {
      const id = this.$route.query.branch_id;
      if (!id || !this.branches.length) return;
      this.selectedBranch = this.branches.find((b) => String(b.id) === String(id)) || null;
    },

    mapBranchList(list) {
      return (list || []).map((b) => ({
        id: b.id,
        name: b.full_name || b.name,
        different_days: b.different_days || [],
        different_days_count: b.different_days_count ?? (b.different_days?.length || 0),
      }));
    },

    openCustomBranch(branch) {
      this.mode = "branch";
      this.selectedBranch = { id: branch.id, name: branch.name };
      this.activeDayTab = 0;
    },

    apiParams() {
      if (this.mode === "global") return { scope: "global" };
      return { branch_id: this.selectedBranch?.id };
    },

    async setRows() {
      if (!this.canShowSchedule) {
        this.resetWorkDays();
        return;
      }
      this.loading = true;
      this.resetWorkDays();
      this.customBranches = [];
      try {
        const res = await this.$axios.get("work_day", { params: this.apiParams() });
        if (this.mode === "global" && res.data.meta?.custom_branches) {
          this.customBranches = this.mapBranchList(res.data.meta.custom_branches);
        }
        (res.data.data || []).forEach((el) => {
          const day = this.work_days.find((w) => w.day === String(el.day).toLowerCase());
          if (day) {
            day.id = el.id;
            day.times = el.times || [];
            day.branches = this.mapBranchList(el.branches || []);
            day.custom_branches = this.mapBranchList(el.custom_branches || []);
          }
        });
      } catch (err) {
        this.$iziToast.error({
          timeout: 2000,
          message: err.response?.data?.message || "Failed to load",
          position: "bottomRight",
        });
      } finally {
        this.loading = false;
      }
    },

    newSlot() {
      return {
        ...SLOT_DEFAULTS,
        from: null,
        to: null,
        id: this.createRandomString(),
        new: true,
        uid: this.createRandomString(),
        time_from_modal: false,
        time_to_modal: false,
      };
    },

    editItem(day) {
      const mergedTimes = day.times.length ? this.mergeOvernightSlotsForEdit(day) : [this.newSlot()];
      this.itemtoEdit = {
        ...day,
        times: mergedTimes.map((t) => ({
          ...t,
          is_active: t.is_active !== false,
          is_free: !!t.is_free,
          time_from_modal: false,
          time_to_modal: false,
        })),
      };
      this.dialogEdit = true;
    },

    closeEditModal() {
      this.dialogEdit = false;
      this.itemtoEdit = null;
    },

    appendSlot() {
      this.itemtoEdit.times.push(this.newSlot());
    },

    removeSlot(row, index) {
      if (this.itemtoEdit.times.length <= 1) return;
      this.itemtoEdit.times.splice(index, 1);
    },

    saveTimeFrom(time, index) {
      this.$refs[`time_from_${index}`][0].save(time);
    },

    saveTimeTo(time, index) {
      this.$refs[`time_to_${index}`][0].save(time);
    },

    getNextWeekday(day) {
      const index = WEEKDAYS.indexOf(day);
      return WEEKDAYS[(index + 1) % WEEKDAYS.length];
    },

    timeToMinutes(time) {
      if (!time) return 0;
      const [hours, minutes] = String(time).slice(0, 5).split(":").map(Number);
      return hours * 60 + minutes;
    },

    isOvernightSlot(row) {
      return !!(row.from && row.to && this.timeToMinutes(row.to) <= this.timeToMinutes(row.from));
    },

    normalizeTimeLabel(time) {
      return time ? String(time).slice(0, 5) : null;
    },

    slotMetaMatches(a, b) {
      return Number(a.price ?? 0) === Number(b.price ?? 0)
        && !!a.is_free === !!b.is_free
        && (a.is_active !== false) === (b.is_active !== false)
        && Number(a.num_of_orders) === Number(b.num_of_orders);
    },

    mergeOvernightSlotsForEdit(day) {
      const nextDay = this.work_days.find((d) => d.day === this.getNextWeekday(day.day));
      if (!nextDay || !nextDay.times.length) return [...day.times];

      const usedNext = new Set();
      const merged = [];

      day.times.forEach((slot) => {
        if (this.normalizeTimeLabel(slot.to) === "23:59") {
          const continuation = nextDay.times.find((nextSlot) => {
            if (usedNext.has(nextSlot.id)) return false;
            return this.normalizeTimeLabel(nextSlot.from) === "00:00"
              && this.slotMetaMatches(slot, nextSlot);
          });

          if (continuation) {
            usedNext.add(continuation.id);
            merged.push({
              ...slot,
              to: this.normalizeTimeLabel(continuation.to),
            });
            return;
          }
        }

        merged.push(slot);
      });

      return merged;
    },

    validateSlots() {
      for (const el of this.itemtoEdit.times) {
        if (!el.from || !el.to) {
          this.$iziToast.error({ timeout: 2000, message: this.$t("forms.validation.times"), position: "bottomRight" });
          return false;
        }
        if (el.price === null || el.price === "" || el.price === undefined) {
          this.$iziToast.error({ timeout: 2000, message: this.$t("forms.validation.prices"), position: "bottomRight" });
          return false;
        }
        if (!el.num_of_orders) {
          this.$iziToast.error({ timeout: 2000, message: this.$t("forms.validation.num_of_orders"), position: "bottomRight" });
          return false;
        }
      }
      return true;
    },

    buildWorkDayForm(day, times) {
      const frm = new FormData();
      frm.append("day", day.day);

      if (this.mode === "global") {
        frm.append("apply_to_all", 1);
      } else {
        frm.append("branches[0][id]", this.selectedBranch.id);
      }

      times.forEach((el, i) => {
        frm.append(`workTimes[${i}][from]`, el.from);
        frm.append(`workTimes[${i}][to]`, el.to);
        frm.append(`workTimes[${i}][price]`, el.price ?? 0);
        frm.append(`workTimes[${i}][num_of_orders]`, el.num_of_orders);
        frm.append(`workTimes[${i}][is_active]`, el.is_active !== false ? 1 : 0);
        frm.append(`workTimes[${i}][is_free]`, el.is_free ? 1 : 0);
      });

      let url = "work_day";
      if (day.id) {
        frm.append("_method", "PUT");
        url = `work_day/${day.id}`;
      }

      return { frm, url };
    },

    async persistDaySlots(day, times) {
      const { frm, url } = this.buildWorkDayForm(day, times);
      await this.$axios.post(url, frm, { headers: { "Content-Type": "multipart/form-data" } });
    },

    findDayForSlot(slot) {
      return this.work_days.find((d) => d.times.some((t) => String(t.id) === String(slot.id)));
    },

    async saveForm() {
      if (!this.validateSlots()) return;
      if (this.mode === "branch" && !this.selectedBranch) {
        this.$iziToast.error({ timeout: 2000, message: this.$t("forms.validation.branch"), position: "bottomRight" });
        return;
      }

      this.btnIsLoading = true;
      try {
        await this.persistDaySlots(this.itemtoEdit, this.itemtoEdit.times);
        this.closeEditModal();
        await this.setRows();
        this.$iziToast.success({ timeout: 2000, message: this.$t("editSuccess"), position: "bottomRight" });
      } catch (err) {
        this.$iziToast.error({
          timeout: 2000,
          message: err.response?.data?.message || err.response?.data?.messages || "Save failed",
          position: "bottomRight",
        });
      } finally {
        this.btnIsLoading = false;
      }
    },

    deleteSlot(slot) {
      this.deleteTarget = slot;
      this.deleteType = "slot";
      this.dialogDelete = true;
    },

    deleteDay(day) {
      this.deleteTarget = day;
      this.deleteType = "day";
      this.dialogDelete = true;
    },

    async confirmDelete() {
      if (!this.deleteTarget) return;
      this.btnIsLoading = true;
      try {
        if (this.deleteType === "slot") {
          if (this.mode === "global") {
            const day = this.findDayForSlot(this.deleteTarget);
            if (!day) {
              throw new Error("Day not found");
            }
            await this.$axios.post("work_day/delete-shared-slot", {
              branch_work_day_id: day.id,
              from: this.deleteTarget.from,
              to: this.deleteTarget.to,
            });
          } else {
            await this.$axios.delete(`work_day/time/${this.deleteTarget.id}`);
          }
        } else {
          const params = this.mode === "global"
            ? { apply_to_all: 1 }
            : { branch_id: this.selectedBranch.id };
          await this.$axios.delete(`work_day/${this.deleteTarget.id}`, { params });
        }
        this.dialogDelete = false;
        this.deleteTarget = null;
        await this.setRows();
        this.$iziToast.success({ timeout: 2000, message: this.$t("deleteSuccess"), position: "bottomRight" });
      } catch (err) {
        this.$iziToast.error({
          timeout: 2000,
          message: err.response?.data?.message || "Delete failed",
          position: "bottomRight",
        });
      } finally {
        this.btnIsLoading = false;
      }
    },

    openBulkDelete() {
      this.bulkDeleteType = "all";
      this.bulkDeleteBranches = [];
      this.dialogBulkDelete = true;
    },

    async confirmBulkDelete() {
      if (this.bulkDeleteType === "branches" && !this.bulkDeleteBranches.length) {
        this.$iziToast.error({ timeout: 2000, message: this.$t("forms.validation.branch"), position: "bottomRight" });
        return;
      }
      this.btnIsLoading = true;
      try {
        const payload = { scope: this.bulkDeleteType };
        if (this.bulkDeleteType === "branches") {
          payload.branch_ids = this.bulkDeleteBranches.map((b) => b.id);
        }
        await this.$axios.post("work_day/bulk-delete", payload);
        this.dialogBulkDelete = false;
        await this.setRows();
        this.$iziToast.success({ timeout: 2000, message: this.$t("deleteSuccess"), position: "bottomRight" });
      } catch (err) {
        const errors = err.response?.data?.errors;
        const message = errors
          ? Object.values(errors).flat().join(", ")
          : err.response?.data?.message || "Delete failed";
        this.$iziToast.error({ timeout: 3000, message, position: "bottomRight" });
      } finally {
        this.btnIsLoading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.work_times {
  --wt-radius: 14px;
  --wt-radius-sm: 10px;
  --wt-border: #e4e9ef;
  --wt-bg: #f5f7fa;
  --wt-card: #ffffff;
  --wt-text: #1a2332;
  --wt-muted: #6b7a8d;
  --wt-shadow: 0 4px 20px rgba(26, 35, 50, 0.06);

  /* ── Toolbar ── */
  .work-toolbar {
    padding: 12px 16px 0;
  }

  .toolbar-card {
    background: var(--wt-card);
    border-radius: var(--wt-radius);
    border: 1px solid var(--wt-border);
    padding: 20px 24px;
    box-shadow: var(--wt-shadow);

    &__top {
      display: grid;
      grid-template-columns: 1fr 1fr auto;
      gap: 20px;
      align-items: start;

      @media (max-width: 991px) {
        grid-template-columns: 1fr;
      }
    }

    &__actions {
      display: flex;
      align-items: flex-end;
      justify-content: flex-end;
      height: 100%;
      padding-bottom: 2px;
    }
  }

  .mode-switch {
    display: inline-flex;
    gap: 4px;
    background: var(--wt-bg);
    border-radius: var(--wt-radius-sm);
    padding: 4px;
    border: 1px solid var(--wt-border);
  }

  .mode-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: none;
    background: transparent;
    border-radius: 8px;
    padding: 10px 18px;
    font-size: 13px;
    font-weight: 600;
    color: var(--wt-muted);
    cursor: pointer;
    transition: all 0.25s ease;
    white-space: nowrap;

    .v-icon {
      opacity: 0.7;
    }

    &.active {
      background: var(--mainColor-2);
      color: #fff;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

      .v-icon {
        opacity: 1;
      }
    }

    &:not(.active):hover {
      background: rgba(0, 0, 0, 0.04);
      color: var(--wt-text);
    }
  }

  .hint-text {
    font-size: 12px;
    color: var(--wt-muted);
    margin: 10px 0 0;
    line-height: 1.6;
    max-width: 360px;
  }

  .branch-select .multiselect {
    min-height: 42px;
  }

  .bulk-delete-btn {
    border-color: #e57373 !important;
    color: #c62828 !important;
    background: #fff5f5 !important;
    text-transform: none !important;
    font-weight: 600 !important;
    letter-spacing: 0 !important;
    border-radius: var(--wt-radius-sm) !important;
    height: 42px !important;

    &:hover {
      background: #ffebee !important;
    }
  }

  /* ── Stats ── */
  .stats-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid var(--wt-border);

    @media (max-width: 767px) {
      grid-template-columns: 1fr;
    }
  }

  .stat-box {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
    background: var(--wt-bg);
    border-radius: var(--wt-radius-sm);
    border: 1px solid var(--wt-border);
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    }

    &__icon {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      &--days { background: linear-gradient(135deg, #42a5f5, #1e88e5); }
      &--slots { background: linear-gradient(135deg, #66bb6a, #43a047); }
      &--global { background: linear-gradient(135deg, var(--mainColor-2), var(--mainColor-2_2, var(--mainColor-2))); }
      &--branch { background: linear-gradient(135deg, #ffa726, #f57c00); }
    }

    &__content {
      display: flex;
      flex-direction: column;
      min-width: 0;

      strong {
        font-size: 20px;
        font-weight: 800;
        color: var(--wt-text);
        line-height: 1.2;

        small {
          font-size: 14px;
          font-weight: 600;
          color: var(--wt-muted);
        }
      }

      .stat-box__mode {
        font-size: 14px !important;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 160px;
      }

      > span:last-child {
        font-size: 11px;
        color: var(--wt-muted);
        margin-top: 2px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  .custom-branches-panel {
    margin-top: 16px;
    padding: 14px 16px;
    background: #fff8e1;
    border: 1px solid #ffe082;
    border-radius: var(--wt-radius-sm);

    &__head {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
      font-weight: 700;
      color: #e65100;
    }

    &__count {
      margin-inline-start: auto;
      min-width: 22px;
      height: 22px;
      padding: 0 7px;
      border-radius: 11px;
      background: rgba(230, 81, 0, 0.15);
      font-size: 11px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    &__hint {
      margin: 6px 0 10px;
      font-size: 12px;
      color: #bf360c;
      opacity: 0.85;
    }

    &__list {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }
  }

  .custom-branch-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border: 1px solid #ffcc80;
    border-radius: 20px;
    background: #fff;
    color: #e65100;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      border-color: #fb8c00;
      background: #fff3e0;
    }

    &__name {
      max-width: 180px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__meta {
      font-size: 10px;
      font-weight: 700;
      padding: 2px 8px;
      border-radius: 10px;
      background: rgba(230, 81, 0, 0.12);
    }
  }

  /* ── Empty ── */
  .empty-panel {
    padding: 32px 16px;

    &__card {
      max-width: 400px;
      margin: 0 auto;
      text-align: center;
      padding: 48px 32px;
      background: var(--wt-card);
      border: 2px dashed var(--wt-border);
      border-radius: var(--wt-radius);
    }

    &__icon {
      width: 72px;
      height: 72px;
      margin: 0 auto 16px;
      background: rgba(var(--mainColor-2-rgb, 2, 169, 224), 0.1);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    h5 {
      margin: 0 0 8px;
      color: var(--wt-text);
      font-weight: 700;
    }

    p {
      color: var(--wt-muted);
      font-size: 14px;
      margin: 0;
    }
  }

  /* ── Schedule ── */
  .schedule-section {
    padding: 12px 16px 24px;
  }

  .week-strip {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding: 4px 2px 12px;
    margin-bottom: 4px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    &__item {
      flex: 0 0 auto;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      min-width: 72px;
      padding: 10px 8px;
      border: 1px solid var(--wt-border);
      border-radius: var(--wt-radius-sm);
      background: var(--wt-card);
      cursor: pointer;
      transition: all 0.2s;
      position: relative;

      &.configured:not(.active) {
        border-color: rgba(var(--mainColor-2-rgb, 2, 169, 224), 0.4);
        background: rgba(var(--mainColor-2-rgb, 2, 169, 224), 0.04);
      }

      &.active {
        border-color: var(--mainColor-2);
        background: var(--mainColor-2);
        color: #fff;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);

        .week-strip__dot {
          background: #fff;
          color: var(--mainColor-2);
        }
      }

      &:hover:not(.active) {
        border-color: var(--mainColor-2);
      }
    }

    &__name {
      font-size: 11px;
      font-weight: 700;
      text-transform: capitalize;
      white-space: nowrap;
    }

    &__dot {
      font-size: 10px;
      font-weight: 800;
      background: var(--mainColor-2);
      color: #fff;
      min-width: 18px;
      height: 18px;
      border-radius: 9px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 5px;
    }
  }

  .tabs-wrapper {
    background: var(--wt-card);
    border: 1px solid var(--wt-border);
    border-radius: var(--wt-radius-sm) var(--wt-radius-sm) 0 0;
    padding: 0 8px;
    border-bottom: none;
  }

  .weekday-tabs {
    .v-tab {
      text-transform: none !important;
      font-weight: 600 !important;
      font-size: 13px !important;
      letter-spacing: 0 !important;
      min-width: 80px !important;
    }

    .v-tabs-slider {
      height: 3px !important;
      border-radius: 3px 3px 0 0;
    }

    .tab-day-name {
      margin-inline-end: 6px;
    }

    .tab-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 18px;
      height: 18px;
      padding: 0 5px;
      border-radius: 9px;
      font-size: 10px;
      font-weight: 800;
      background: var(--mainColor-2);
      color: #fff;

      &.empty {
        background: #e0e5eb;
        color: #9aa5b4;
      }
    }
  }

  .day-panel {
    border-radius: 0 0 var(--wt-radius) var(--wt-radius) !important;
    border: 1px solid var(--wt-border) !important;
    border-top: none !important;
    overflow: hidden;
    box-shadow: var(--wt-shadow);

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 16px;
      padding: 18px 24px;
      background: linear-gradient(135deg, var(--mainColor-2) 0%, var(--mainColor-2_2, var(--mainColor-2)) 100%);
      color: #fff;
    }

    &__info {
      display: flex;
      align-items: center;
      gap: 14px;
    }

    &__icon {
      width: 44px;
      height: 44px;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.2);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &__title {
      margin: 0;
      font-size: 20px;
      font-weight: 800;
    }

    &__meta {
      font-size: 12px;
      opacity: 0.9;
    }

    &__actions {
      display: flex;
      gap: 8px;
    }

    &__body {
      background: var(--wt-card);
    }
  }

  .day-custom-branches {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #fff8e1;
    border-bottom: 1px solid #ffe082;

    &__label {
      font-size: 12px;
      font-weight: 700;
      color: #e65100;
    }
  }

  .day-custom-branch-tag {
    border: none;
    padding: 4px 10px;
    border-radius: 14px;
    background: #fff;
    color: #e65100;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid #ffcc80;
    transition: background 0.2s;

    &:hover {
      background: #fff3e0;
    }
  }

  .action-btn {
    text-transform: none !important;
    font-weight: 600 !important;
    letter-spacing: 0 !important;
    border-radius: 8px !important;
    height: 36px !important;

    &--primary {
      background: #fff !important;
      color: var(--mainColor-2) !important;
    }

    &--danger {
      border-color: rgba(255, 255, 255, 0.6) !important;
      color: #fff !important;
    }
  }

  .day-table {
    padding: 4px 12px 12px;

    thead th {
      font-size: 12px !important;
      font-weight: 700 !important;
      color: var(--wt-muted) !important;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }

    tbody tr:hover {
      background: rgba(var(--mainColor-2-rgb, 2, 169, 224), 0.04) !important;
    }
  }

  .row-index {
    display: inline-flex;
    width: 24px;
    height: 24px;
    align-items: center;
    justify-content: center;
    background: var(--wt-bg);
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    color: var(--wt-muted);
  }

  .time-range {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-weight: 700;
    font-size: 13px;
    color: var(--wt-text);

    &__from,
    &__to {
      background: var(--wt-bg);
      padding: 4px 10px;
      border-radius: 6px;
      font-variant-numeric: tabular-nums;
    }

    &__arrow {
      color: var(--mainColor-2) !important;
      opacity: 0.7;
    }
  }

  .price-cell {
    font-weight: 600;
    color: var(--wt-text);
  }

  .table-delete-btn {
    opacity: 0.6;
    transition: opacity 0.2s;

    &:hover {
      opacity: 1;
    }
  }

  .day-empty {
    padding: 56px 24px;
    background: var(--wt-bg);

    &__inner {
      text-align: center;
      max-width: 280px;
      margin: 0 auto;
    }

    &__icon {
      color: var(--mainColor-2) !important;
      opacity: 0.35;
      margin-bottom: 12px;
    }

    p {
      color: var(--wt-muted);
      margin: 0 0 20px;
      font-size: 14px;
    }
  }
}

/* Dialogs render outside .work_times (v-dialog teleports to body) */
.work-time-dialog {
  --wt-radius: 14px;
  --wt-radius-sm: 10px;
  --wt-border: #e4e9ef;
  --wt-bg: #f5f7fa;
  --wt-card: #ffffff;
  --wt-text: #1a2332;
  --wt-muted: #6b7a8d;

  border-radius: 14px;
  overflow: hidden;

  .edit-modal {
    border-radius: var(--wt-radius) !important;
    overflow: hidden;
    box-shadow: 0 24px 48px rgba(0, 0, 0, 0.18) !important;

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 20px 24px;
      background: linear-gradient(135deg, var(--mainColor-2), var(--mainColor-2_2, var(--mainColor-2)));
      color: #fff;
    }

    &__header-content {
      display: flex;
      align-items: center;
      gap: 14px;
    }

    &__day-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.2);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &__label {
      display: block;
      font-size: 11px;
      opacity: 0.85;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      font-weight: 600;
    }

    &__title {
      margin: 2px 0 0;
      font-size: 22px;
      font-weight: 800;
    }

    &__close {
      background: rgba(255, 255, 255, 0.15) !important;
    }

    &__body {
      padding: 20px 24px !important;
      max-height: 58vh;
      background: var(--wt-bg);
    }

    &__footer {
      padding: 14px 20px !important;
      background: var(--wt-card);
      border-top: 1px solid var(--wt-border);
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 10px;
    }

    &__btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      min-width: 120px;
      height: 42px;
      padding: 0 20px;
      border-radius: 10px;
      font-size: 14px;
      font-weight: 600;
      line-height: 1;
      cursor: pointer;
      transition: background 0.2s, border-color 0.2s, opacity 0.2s, transform 0.15s;
      border: none;
      outline: none;

      &:active:not(:disabled) {
        transform: scale(0.98);
      }

      &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
      }

      &--cancel {
        background: var(--wt-bg);
        color: var(--wt-muted);
        border: 1.5px solid var(--wt-border);

        &:hover:not(:disabled) {
          border-color: var(--mainColor-2);
          color: var(--mainColor-2);
          background: rgba(var(--mainColor-2-rgb, 2, 169, 224), 0.05);
        }
      }

      &--save {
        background: var(--mainColor-2);
        color: #fff;
        box-shadow: 0 4px 14px rgba(var(--mainColor-2-rgb, 2, 169, 224), 0.35);

        &:hover:not(:disabled) {
          filter: brightness(1.05);
          box-shadow: 0 6px 18px rgba(var(--mainColor-2-rgb, 2, 169, 224), 0.45);
        }
      }

      &-spinner {
        width: 16px;
        height: 16px;
        border: 2px solid rgba(255, 255, 255, 0.35);
        border-top-color: #fff;
        border-radius: 50%;
        animation: wt-spin 0.7s linear infinite;
        flex-shrink: 0;
      }
    }
  }

  @keyframes wt-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .scope-banner {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px 16px;
    border-radius: var(--wt-radius-sm);
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 16px;
    border: 1px solid transparent;

    &.global {
      background: #e8f4fd;
      border-color: #bbdefb;
      color: #1565c0;
    }

    &.branch {
      background: #fff8e1;
      border-color: #ffe082;
      color: #e65100;
    }

    .branch-tag {
      margin-inline-start: auto;
      background: rgba(0, 0, 0, 0.08);
      padding: 3px 12px;
      border-radius: 20px;
      font-weight: 700;
      font-size: 12px;
      white-space: nowrap;
    }
  }

  .slot-card {
    position: relative;
    display: flex;
    background: var(--wt-card);
    border: 1px solid var(--wt-border);
    border-radius: var(--wt-radius-sm);
    margin-bottom: 12px;
    overflow: hidden;
    transition: box-shadow 0.2s;

    &:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    }

    &__accent {
      width: 4px;
      flex-shrink: 0;
      background: linear-gradient(180deg, var(--mainColor-2), var(--mainColor-2_2, var(--mainColor-2)));
    }

    &__inner {
      flex: 1;
      padding: 16px 18px;
      min-width: 0;
    }

    &__head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 14px;
    }

    &__num-wrap {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    &__num {
      width: 28px;
      height: 28px;
      border-radius: 8px;
      background: var(--mainColor-2);
      color: #fff;
      font-size: 13px;
      font-weight: 800;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    &__label {
      font-size: 13px;
      font-weight: 700;
      color: var(--wt-text);
    }

    &__remove {
      opacity: 0.5;
      transition: opacity 0.2s;

      &:hover:not(:disabled) {
        opacity: 1;
      }
    }

    &__time-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      margin-bottom: 4px;

      @media (max-width: 480px) {
        grid-template-columns: 1fr;
      }
    }
  }

  .field-label {
    display: block;
    font-size: 11px;
    font-weight: 700;
    color: var(--wt-muted);
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-bottom: 6px;
  }

  .time-field {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 11px 14px;
    border: 1.5px solid var(--wt-border);
    border-radius: var(--wt-radius-sm);
    background: var(--wt-bg);
    cursor: pointer;
    font-weight: 700;
    font-size: 15px;
    font-variant-numeric: tabular-nums;
    color: var(--wt-muted);
    transition: all 0.2s;

    &.filled {
      color: var(--wt-text);
      background: var(--wt-card);
      border-color: var(--mainColor-2);
    }

    &:hover {
      border-color: var(--mainColor-2);
    }

    .v-icon {
      color: var(--mainColor-2);
      opacity: 0.7;
    }
  }

  .slot-input.input_wrapper {
    margin-bottom: 0 !important;
  }

  .overnight-hint {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 10px 0 4px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #ede7f6;
    border: 1px solid #d1c4e9;
    color: #4527a0;
    font-size: 12px;
    line-height: 1.45;

    .v-icon {
      margin-top: 2px;
      flex-shrink: 0;
    }
  }

  .slot-toggles {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding-top: 4px;
  }

  .toggle-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border-radius: 20px;
    border: 1.5px solid var(--wt-border);
    background: var(--wt-bg);
    font-size: 12px;
    font-weight: 600;
    color: var(--wt-muted);
    cursor: pointer;
    transition: all 0.2s;
    margin: 0;
    user-select: none;

    &.on {
      border-color: var(--mainColor-2);
      background: rgba(var(--mainColor-2-rgb, 2, 169, 224), 0.1);
      color: var(--mainColor-2);
    }

    &:hover {
      border-color: var(--mainColor-2);
    }
  }

  .add-slot-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 14px;
    margin-top: 4px;
    border: 2px dashed var(--wt-border);
    border-radius: var(--wt-radius-sm);
    background: transparent;
    color: var(--mainColor-2);
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      border-color: var(--mainColor-2);
      background: rgba(var(--mainColor-2-rgb, 2, 169, 224), 0.05);
    }
  }

  .confirm-dialog {
    border-radius: var(--wt-radius) !important;

    .v-card__title {
      font-size: 16px;
      font-weight: 700;
      padding-top: 24px;
    }
  }
}

/* RTL */
body.rtl .work_times {
  .time-range__arrow {
    transform: scaleX(-1);
  }
}

body.rtl .work-time-dialog {
  .scope-banner .branch-tag {
    margin-inline-start: 0;
    margin-inline-end: auto;
  }
}

/* Dark theme */
body.darkTheme .work_times {
  --wt-border: #3a3f4b;
  --wt-bg: #1e2128;
  --wt-card: #262a33;
  --wt-text: #e8eaed;
  --wt-muted: #9aa0a6;

  .toolbar-card,
  .tabs-wrapper,
  .day-panel__body {
    background: var(--wt-card);
  }

  .stat-box,
  .week-strip__item {
    background: var(--wt-bg);
  }

  .day-empty {
    background: var(--wt-bg);
  }
}

body.darkTheme .work-time-dialog {
  --wt-border: #3a3f4b;
  --wt-bg: #1e2128;
  --wt-card: #262a33;
  --wt-text: #e8eaed;
  --wt-muted: #9aa0a6;

  .edit-modal__footer {
    background: var(--wt-card);
  }

  .edit-modal__btn--cancel {
    background: #2a2f38;
    border-color: var(--wt-border);
    color: var(--wt-muted);

    &:hover:not(:disabled) {
      background: rgba(var(--mainColor-2-rgb, 2, 169, 224), 0.1);
    }
  }

  .slot-card,
  .time-field.filled {
    background: var(--wt-bg);
  }

  .edit-modal__body {
    background: #1a1d24;
  }

  .scope-banner.global {
    background: rgba(21, 101, 192, 0.15);
    border-color: rgba(21, 101, 192, 0.3);
  }

  .scope-banner.branch {
    background: rgba(230, 81, 0, 0.12);
    border-color: rgba(230, 81, 0, 0.25);
  }
}
</style>
